import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function Unsubscribe() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lead, setLead] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [unsubscribeCompany, setUnsubscribeCompany] = useState(false);

  useEffect(() => {
    fetchLeadInfo();
  }, [token]);

  const fetchLeadInfo = async () => {
    try {
      setLoading(true);
      // The backend should return lead info when GET /unsubscribe/:token is called
      // For now, we'll decode the token to get email (matching backend logic)
      // In production, you'd want to fetch from an API endpoint
      setLoading(false);
    } catch (error) {
      console.error('Error fetching lead info:', error);
      setError('Failed to load unsubscribe page');
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch(`${API_BASE.replace('/api', '')}/unsubscribe/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          unsubscribe_company: unsubscribeCompany
        })
      });

      if (!response.ok) {
        throw new Error('Failed to unsubscribe');
      }

      const data = await response.json();
      setSuccess(true);
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setError('Failed to unsubscribe. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading unsubscribe page...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribed Successfully</h1>
          <p className="text-gray-600 mb-6">
            You have been successfully unsubscribed from our email communications.
            {unsubscribeCompany && ' All emails from your company have also been unsubscribed.'}
          </p>
          <p className="text-sm text-gray-500">
            This may take a few minutes to take effect. If you continue to receive emails,
            please contact our support team.
          </p>
          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              LeadForge<br />
              123 Main St, Anytown, USA 12345
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Unsubscribe from Emails</h1>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <XCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <p className="text-gray-600 mb-6">
          We're sorry to see you go. You can unsubscribe from our email communications below.
        </p>

        <div className="space-y-4 mb-6">
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={!unsubscribeCompany}
              onChange={(e) => setUnsubscribeCompany(!e.target.checked)}
              className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm text-gray-700">
              Unsubscribe just my email address
            </span>
          </label>

          <label className="flex items-start">
            <input
              type="checkbox"
              checked={unsubscribeCompany}
              onChange={(e) => setUnsubscribeCompany(e.target.checked)}
              className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <span className="ml-3 text-sm text-gray-700">
              Unsubscribe all emails from my company
              <span className="block text-xs text-gray-500 mt-1">
                This will unsubscribe all email addresses associated with your company domain.
              </span>
            </span>
          </label>
        </div>

        <button
          onClick={handleUnsubscribe}
          disabled={submitting}
          className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {submitting ? (
            <>
              <Loader2 className="animate-spin h-5 w-5 mr-2" />
              Processing...
            </>
          ) : (
            'Confirm Unsubscribe'
          )}
        </button>

        <p className="mt-6 text-xs text-gray-500 text-center">
          This action is permanent and will stop all email communications immediately.
          You can resubscribe by contacting our support team.
        </p>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            <strong>LeadForge</strong><br />
            123 Main St, Anytown, USA 12345<br />
            <a href="mailto:support@leadforge.com" className="text-purple-600 hover:text-purple-700">
              support@leadforge.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}


